export type ApiTerm = {
  sn?: number
  mainCategory?: string
  subCategory?: string
  isRequired?: boolean
  sortNumber?: number
  division?: string
  title?: string
  content?: string
  type?: string
}

export type CounselRequestTermHistory = {
  /**
   * @deprecated INT-209 이후 미전송. CRM 이 sn + type 동시 수신 시 sn 우선(하위호환)이라
   * 남기면 type 해석이 무시된다. 단일 채널 정책(필드 제거 금지)상 타입에만 남김.
   */
  customerApplicationManagementSn?: number
  phoneNumber?: string
  isAgree?: boolean
  division?: string
  /** CRM 약관 구분 codeId (TERMS_TYPE_CODE). CRM 이 상담 테넌트/mainCategory 기준 "사용중" 약관 sn 으로 치환 저장 */
  type?: string
}

export type CounselRequestLifeCareTermsRq = {
  agree01: 'Y' | 'N'
  agree02: 'Y' | 'N'
  agree03: 'Y' | 'N'
  agree04?: 'Y' | 'N'
  agree05?: 'Y' | 'N'
  [key: string]: 'Y' | 'N' | undefined
}

// 야간(20:30~08:00 KST) 수신 동의 — 야간 제출 시 모달로 수집해 body 에 포함
export type CallReceiveConsent = {
  isNightCallReceiveConsent: boolean
  nightCallbackRequestMinute: number | null
}

export type CounselRequestBody = {
  customerName: string | null
  phoneNumber: string
  checkTerms: boolean
  requestUrl: string
  tokenExpireHours: number
  sendAlimTalk: boolean
  applyLifecare: boolean
  termsHistories: CounselRequestTermHistory[]

  distinctId?: string
  mainCategory?: string | null
  additionalCategoryList?: string[] | null
  extraCategories?: string[]
  earlyBirdEventType?: 'GALAXY_S_26'
  receivePath?: string
  inflowPath?: string
  inflowPathDetail?: string
  holdMessage?: string
  additionalSummary?: string
  userSn?: number | null
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  utmPath?: string
  /**
   * 상조타입. 두 형태가 공존한다.
   * - 레거시: 'WON_100'(100원 상조) | 'COMBINED'(결합 상조). 미전달 시 서버가 COMBINED 처리. 허용값 외는 400
   * - 신규 상품타입(sjCode 동반): 자유 라벨 (예: '100원 상조')
   */
  sjType?: string
  /** 신규 상품타입 코드 (예: 'SJ0001'). 이 필드가 있으면 sjType 은 자유 라벨로 해석된다 */
  sjCode?: string
  /** 신규 상품타입 상품명 (예: '100원 상조') */
  sjName?: string
  /** 신규 상품타입 프로모션명 (예: '온사이트 크로스셀링 프로모션') */
  prodName?: string
  lifeCareTermsAgreement?: CounselRequestLifeCareTermsRq
  callReceiveConsent?: CallReceiveConsent
}

export type CounselRequestResponse = {
  code: string
  contractFormId: string
  customerId: string
  finalProgressId: string
  finalProgressName: string
  hashString: string
  isExistsCounsel: boolean
  errorCode?: string
  errorMessage?: string
}

export async function fetchTerms(apiBaseUrl: string): Promise<ApiTerm[]> {
  const res = await fetch(`${apiBaseUrl}/counsel-request/terms`, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`GET terms failed: ${res.status}`)
  return res.json() as Promise<ApiTerm[]>
}

export type ServerTimeResponse = {
  epochMillis: number
  serverDateTime: string
  zoneId: string
}

// 서버 실시각(KST 기준) 조회. 야간 판정용. 인증 불필요·no-store.
// 실패 시 null → 호출부가 로컬시각으로 fallback.
export async function fetchServerNow(apiBaseUrl: string): Promise<number | null> {
  try {
    const res = await fetch(`${apiBaseUrl}/common/server-time`, { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as ServerTimeResponse
    return typeof data.epochMillis === 'number' ? data.epochMillis : null
  } catch {
    return null
  }
}

export async function submitCounselRequest(
  apiBaseUrl: string,
  body: CounselRequestBody,
): Promise<CounselRequestResponse> {
  const res = await fetch(`${apiBaseUrl}/v1/marketing/counsel-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`POST counsel-request failed: ${res.status}`)
  // extra-only(메인/상조 없음) 제출은 서버가 200 + 빈 본문으로 응답 → res.json() 이 터짐.
  // 빈 본문/비 JSON 을 허용해 빈 객체로 처리 (hashString 등은 해당 케이스에서 불필요).
  const text = (await res.text()).trim()
  return (text ? JSON.parse(text) : {}) as CounselRequestResponse
}

export type EncryptPhoneNumberResponse = {
  phoneHash: string
}

export async function encryptPhoneNumber(
  apiBaseUrl: string,
  phoneNumber: string,
): Promise<string> {
  if (!phoneNumber) return ''
  try {
    const res = await fetch(`${apiBaseUrl}/encrypt/phone-number`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber }),
    })
    if (!res.ok) throw new Error(`POST encrypt failed: ${res.status}`)
    const data = (await res.json()) as EncryptPhoneNumberResponse
    return data.phoneHash ?? ''
  } catch (err) {
    console.warn('[ajd-secret-form] encrypt phone-number failed:', err)
    return ''
  }
}
